(() => {
  if (customElements.get('cb-product-room')) return;
  const parseSize = (value, posterCount = 1, combined = false, poster = false) => {
    const raw = String(value);
    const dimensions = posterCount > 1 ? raw.trim().replace(new RegExp(`^${posterCount}\\s*[x×]\\s*`, 'i'), '') : raw.trim();
    const match = dimensions.match(combined || poster ? /^B?(\d+(?:[.,]\d+)?)\s*(?:cm\s*)?[x×]\s*H?(\d+(?:[.,]\d+)?)\s*(?:cm)?$/i : /^(\d+(?:[.,]\d+)?)\s*(?:cm\s*)?[x×]\s*(\d+(?:[.,]\d+)?)\s*cm$/i);
    if (!match) return null;
    const [short, long] = match.slice(1).map(n => Number(n.replace(',', '.'))).sort((a, b) => a - b);
    if (!short || long > 500) return null;
    const [setWidth,setHeight] = match.slice(1).map(n => Number(n.replace(',', '.')));
    return {raw,short,long,...(combined ? {setWidth,setHeight} : {}),label:combined ? `${setWidth} × ${setHeight} cm` : `${posterCount > 1 ? posterCount + ' × ' : ''}${short} × ${long} cm`};
  };
  const portrait = [
    {x:.46,y:.375,ratio:1,span:.84,cm:260,maxBottom:.675,ref:'ceilingWarm'},
    {x:.69,y:.43,ratio:1,span:.628,cm:200,maxBottom:.75,ref:'door'},
    {x:.34,y:.40,ratio:1,span:.853,cm:250,maxBottom:.73,ref:'ceiling'}
  ];
  const landscape = [
    {x:.46845,y:.33,ratio:1.0498687664041995,widthSpan:.6364883401920439,cm:200,maxBottom:.535,ref:'sofa'},
    {x:.50,y:.34,ratio:1,span:770/1254,cm:210,maxBottom:.55,ref:'doorFrame'},
    {x:.50,y:.30,ratio:1,widthSpan:.52,cm:180,maxBottom:.50,shadow:'-3px 5px 7px #0003',ref:'bed'}
  ];
  class ProductRoom extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-room-config]').textContent);
      const parseOptionSize = value => parseSize(value, this.config.posterCount || 1, this.config.posterSizeMode === 'combined', this.config.poster);
      if (this.config.fixedPosterSize) this.config.options.push({values:[this.config.fixedPosterSize],selected:this.config.fixedPosterSize});
      const option = this.config.options.find(o => o.values.length && o.values.every(parseOptionSize));
      if (!option) return;
      this.sizes = option.values.map(parseOptionSize);
      this.current = this.sizes.find(s => s.raw === option.selected) || this.sizes[0];
      this.section = this.closest('.shopify-section') || document;
      this.sceneIndex = 0;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('.cb-product-room-open');
      this.scene = this.querySelector('.cb-product-room-scene');
      this.bg = this.querySelector('.cb-product-room-bg');
      this.art = this.querySelector('.cb-product-room-art');
      this.select = this.querySelector('[data-room-size]');
      this.sizes.forEach(size => this.select.add(new Option(size.label, size.raw)));
      this.sizePicker = this.querySelector('.cb-product-room-size-picker');
      this.sizeSummary = this.sizePicker.querySelector('summary');
      this.sizeList = this.sizePicker.querySelector('.cb-product-room-size-list');
      this.sizes.forEach(size => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = size.label;
        button.dataset.roomSizeOption = size.raw;
        button.addEventListener('click', () => {
          this.select.value = size.raw;
          this.select.dispatchEvent(new Event('change', {bubbles:true}));
          this.sizePicker.open = false;
          this.sizeSummary.focus({preventScroll:true});
        });
        this.sizeList.append(button);
      });
      this.sizePicker.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.sizePicker.open) {
          event.preventDefault();
          this.sizePicker.open = false;
          this.sizeSummary.focus({preventScroll:true});
        }
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && document.activeElement === this.sizeSummary) {
          event.preventDefault();
          this.sizePicker.open = true;
          const buttons = this.sizeList.querySelectorAll('button');
          const chosen = [...buttons].find(button => button.dataset.roomSizeOption === this.current.raw);
          (chosen || buttons[0])?.focus({preventScroll:true});
        }
      });
      this.dataset.inputMode = 'pointer';
      this.onPointer = () => { this.dataset.inputMode = 'pointer'; };
      this.onKey = event => {
        if (event.key === 'Tab' || event.key.startsWith('Arrow')) this.dataset.inputMode = 'keyboard';
      };
      document.addEventListener('pointerdown', this.onPointer, true);
      document.addEventListener('keydown', this.onKey, true);
      this.trigger.addEventListener('click', () => {
        const selected = this.sizeField()?.querySelector('input:checked')?.value || this.sizeSelect()?.value;
        this.current = this.sizes.find(s => s.raw === selected) || this.current;
        const image = this.art.querySelector('img');
        if (!image.getAttribute('src')) image.src = this.config.image;
        this.dialog.showModal();
        this.sizePicker.open = false;
        this.querySelector('strong').focus({preventScroll:true});
        this.draw();
      });
      this.querySelector('.cb-product-room-close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => this.trigger.focus({preventScroll:true}));
      this.querySelectorAll('[data-room-index]').forEach(button => button.addEventListener('click', () => {
        this.sceneIndex = Number(button.dataset.roomIndex);
        this.draw();
      }));
      this.select.addEventListener('change', event => {
        event.stopPropagation();
        this.current = this.sizes.find(s => s.raw === this.select.value) || this.current;
        this.syncSizePicker();
        const radio = [...(this.sizeField()?.querySelectorAll('input[type=radio]') || [])].find(input => input.value === this.current.raw);
        if (radio) radio.click();
        else {
          const select = this.sizeSelect();
          if (select) { select.value = this.current.raw; select.dispatchEvent(new Event('change', {bubbles:true})); }
        }
        this.draw();
      });
      this.onChange = event => {
        if (this.contains(event.target)) return;
        const selected = this.sizeField()?.querySelector('input:checked')?.value || this.sizeSelect()?.value;
        this.current = this.sizes.find(s => s.raw === selected) || this.current;
        if (event.target.matches?.('.addon-frame-input')) this.galleryFrameActive = !!this.frameColor();
        requestAnimationFrame(() => {
          this.drawGalleryFrame();
          this.drawGalleryTemplate();
          if (this.dialog.open) this.draw();
        });
      };
      this.section.addEventListener('change', this.onChange, true);
      this.section.addEventListener('variant:change', this.onChange);
      this.gallery = this.closest('.product__media-wrapper')?.querySelector('.product__media-list');
      this.galleryWrapper = this.gallery?.closest('.product__media-list-wrapper');
      this.onGalleryBrowse = event => {
        if (event.target.closest('.cb-gallery-frame-preview, .cb-favorite-toggle')) return;
        this.galleryFrameActive = false;
        this.drawGalleryFrame();
      };
      this.galleryWrapper?.addEventListener('click', this.onGalleryBrowse, true);
      this.galleryFrameActive = !!this.frameColor();
      this.resize = new ResizeObserver(() => {
        this.alignTrigger();
        this.drawGalleryFrame();
        this.drawGalleryTemplate();
        if (this.dialog.open) this.draw();
      });
      this.resize.observe(this.scene);
      if (this.gallery) this.resize.observe(this.gallery);
      this.hidden = false;
      this.drawGalleryTemplate();
    }
    syncSizePicker() {
      this.sizeSummary.textContent = this.current.label;
      this.sizeSummary.setAttribute('aria-label', this.sizeSummary.dataset.label + ': ' + this.current.label);
      this.sizeList.querySelectorAll('button').forEach(button => {
        if (button.dataset.roomSizeOption === this.current.raw) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }
    drawGalleryFrame() {
      const color = this.frameColor();
      if (this.config.poster || !this.gallery || !color || !this.galleryFrameActive) {
        if (this.galleryPreview) this.galleryPreview.hidden = true;
        if (this.gallerySlider) this.gallerySlider.inert = false;
        return;
      }
      if (!this.galleryPreview) {
        this.galleryPreview = document.createElement('div');
        this.galleryPreview.className = 'cb-gallery-frame-preview';
        this.galleryArt = document.createElement('div');
        this.galleryArt.className = 'cb-gallery-frame-art';
        const image = document.createElement('img');
        image.alt = this.config.title;
        image.width = this.config.imageWidth;
        image.height = this.config.imageHeight;
        image.decoding = 'async';
        const original = [...this.gallery.querySelectorAll('.product__media img')].find(img => img.src.split('?')[0] === new URL(this.config.image, location.href).href.split('?')[0]);
        image.src = original?.currentSrc || this.config.image;
        this.galleryArt.append(image);
        this.galleryPreview.append(this.galleryArt);
        this.gallery.classList.add('cb-gallery-frame-host');
        this.gallery.append(this.galleryPreview);
        this.gallerySlider = this.gallery.querySelector('.js-media-list');
      }
      this.galleryPreview.hidden = false;
      if (this.gallerySlider) this.gallerySlider.inert = true;
      const wide = this.config.imageWidth > this.config.imageHeight && this.current.short !== this.current.long;
      const [w,h] = wide ? [this.current.long,this.current.short] : [this.current.short,this.current.long];
      const box = this.gallery.getBoundingClientRect();
      const unit = Math.min(box.width * .84 / (w + 2.8), box.height * .84 / (h + 2.8));
      this.galleryArt.style.cssText = `width:${w*unit}px;height:${h*unit}px;padding:${.5*unit}px;border-width:${.9*unit}px;border-color:${color};border-image-source:url("${this.config.frames[color === '#191919' ? 'black' : 'oak']}");`;
    }
    alignTrigger() {
      if (!this.gallery) return;
      const gallery = this.gallery.getBoundingClientRect();
      const host = this.getBoundingClientRect();
      this.style.setProperty('--room-gallery-left', `${Math.max(0,gallery.left-host.left)}px`);
      this.style.setProperty('--room-gallery-right', `${Math.max(0,host.right-gallery.right)}px`);
    }
    sizeField() {
      return [...this.section.querySelectorAll('variant-radios fieldset')].find(field => [...field.querySelectorAll('input[type=radio]')].some(input => this.sizes.some(s => s.raw === input.value)));
    }
    sizeSelect() {
      return [...this.section.querySelectorAll('variant-selects select')].find(select => [...select.options].some(o => this.sizes.some(s => s.raw === o.value)));
    }
    frameColor() {
      const input = this.section.querySelector('.addon-frame-input:checked');
      if (!input?.dataset.addonColor) return null;
      try {
        const variants = JSON.parse(input.closest('.addon-product').querySelector('script[type="application/json"]').textContent);
        if (input.dataset.addonColor === variants.find(v => Number(v.id) === 66227366297949)?.option1) return '#191919';
        if (input.dataset.addonColor === variants.find(v => Number(v.id) === 66227366461789)?.option1) return '#b69b70';
      } catch {}
      const thumbnail = input.closest('.addon-container')?.querySelector('img')?.getAttribute('src') || '';
      if (thumbnail.includes('cb-frame-black') || thumbnail.includes('svart-ram')) return '#191919';
      if (thumbnail.includes('cb-frame-oak') || thumbnail.includes('tra-ram')) return '#b69b70';
      return null;
    }
    roomGeometry(sceneIndex, sceneWidth) {
      const count = this.config.posterCount || 1;
      const imageWide = this.config.imageWidth > this.config.imageHeight && this.current.short !== this.current.long;
      const wide = imageWide || count > 1;
      const [singleWidth,h] = this.current.setWidth ? [this.current.setWidth / count,this.current.setHeight] : imageWide ? [this.current.long,this.current.short] : [this.current.short,this.current.long];
      const w = singleWidth * count + (count - 1) * 4;
      const s = (wide ? landscape : portrait)[sceneIndex];
      const asset = this.config[wide ? 'landscape' : 'portrait'][sceneIndex];
      const baseUnit = 100 * (s.widthSpan ? s.widthSpan * s.ratio : s.span) / s.cm;
      const unit = count > 1 ? Math.min(baseUnit, 90 * s.ratio / w) : baseUnit;
      const color = this.config.poster ? '#191919' : this.frameColor();
      const rail = color ? (this.config.poster ? Math.max(1.2, 1 / (sceneWidth / s.ratio * unit / 100)) : .9) : 0;
      const gap = color && !this.config.poster ? .5 : 0;
      const px = sceneWidth / s.ratio * unit / 100;
      const y = s.maxBottom ? Math.min(s.y * 100, s.maxBottom * 100 - h * unit / 2 - (rail + gap) * unit) : s.y * 100;
      const top = Math.max(3,y-h*unit/2-(rail+gap)*unit);
      const left = Math.max(3,Math.min(s.x*100-w*unit/s.ratio/2-(rail+gap)*unit/s.ratio,97-(w+2*(rail+gap))*unit/s.ratio));
      return {count,wide,s,asset,w,h,unit,color,rail,gap,px,top,left};
    }
    paintRoomArt(art, geometry) {
      const {count,s,w,h,unit,color,rail,gap,px,top,left} = geometry;
      art.style.cssText = `left:${left}%;top:${top}%;width:${w*unit/s.ratio}%;height:${h*unit}%;padding:${gap*px}px;border-width:${rail*px}px;border-color:${color || 'transparent'};background:${color ? '#191919' : 'transparent'};box-shadow:${s.shadow || '3px 5px 7px #0003'};`;
      art.style.borderImageSource = color && !this.config.poster ? `url("${this.config.frames[color === '#191919' ? 'black' : 'oak']}")` : 'none';
      if (count > 1) {
        art.style.setProperty('--poster-room-gap', `${4 * px}px`);
        art.style.setProperty('--poster-room-rail', `${rail * px}px`);
      }
    }
    drawGalleryTemplate() {
      if (!this.config.poster || !this.gallery) return;
      const gallery = this.gallery.closest('cb-poster-gallery');
      gallery?.querySelectorAll('.cb-poster-template').forEach(template => {
        const geometry = this.roomGeometry(0, template.clientWidth || this.gallery.clientWidth || 1);
        template.dataset.sizeSynced = 'true';
        template.style.setProperty('--poster-scene-ratio', geometry.s.ratio);
        const background = template.querySelector(':scope > img');
        if (background.getAttribute('src') !== geometry.asset) background.src = geometry.asset;
        const art = template.querySelector('.cb-poster-template-art');
        art.classList.add('cb-product-room-art');
        art.dataset.posterSet = String(geometry.count);
        this.paintRoomArt(art, geometry);
      });
    }
    draw() {
      const geometry = this.roomGeometry(this.sceneIndex, this.scene.clientWidth);
      const {s,wide,asset} = geometry;
      if (this.bg.getAttribute('src') !== asset) this.bg.src = asset;
      this.scene.style.setProperty('--room-ratio',s.ratio);
      this.paintRoomArt(this.art, geometry);
      this.drawGalleryTemplate();
      this.select.value = this.current.raw;
      this.syncSizePicker();
      this.drawGalleryFrame();
      this.querySelectorAll('[data-room-index]').forEach((button,index) => {
        button.setAttribute('aria-pressed', String(index === this.sceneIndex));
        if (index === 2) button.textContent = this.config.labels[wide ? 'bedroom' : 'light'];
      });
      this.querySelector('.cb-product-room-note').textContent = this.config.labels[s.ref] + ' ' + this.config.labels.note + (this.config.poster ? ' ' + this.config.labels.frameNote : '');
    }
    disconnectedCallback() {
      document.removeEventListener('pointerdown',this.onPointer,true);
      document.removeEventListener('keydown',this.onKey,true);
      this.section?.removeEventListener('change',this.onChange,true);
      this.section?.removeEventListener('variant:change',this.onChange);
      this.resize?.disconnect();
      this.galleryWrapper?.removeEventListener('click',this.onGalleryBrowse,true);
      if (this.gallerySlider) this.gallerySlider.inert = false;
      this.galleryPreview?.remove();
      this.galleryPreview = null;
      this.ready = false;
    }
  }
  customElements.define('cb-product-room',ProductRoom);
})();

